import { tagFinderRegex } from '../constants';

/** Takes in a string and returns tuples of tags that are next to each other,
 * separated by any amount of whitespace.
 * @returns Array of unique tuples of tags that are next to each other,
 * or an empty array if no matches are found.
 * @example
 * detectSideBySideTags('But you speak of {{title}} {{name}}, as if he was in a story that had come to an end.')
 * // => [['title', 'name']]
 */
export const detectSideBySideTags = (input: string): [string, string][] => {
    if (!input) {
        return [];
    }
    const matches: [string, string][] = [];
    const splitterRegex = /\{\{.*?\}\}|\S+/g;
    let match;
    let splitWords = [];

    // This generates the array of words to loop over to check for adjacent tags
    // This is required to account for edge-cases such as having a character directly adjacent to a tag
    while ((match = splitterRegex.exec(input)) !== null) {
        if (match[0].startsWith('{{') && match[0].endsWith('}}')) {
            // If the matched string is a tag, add it to the result array
            splitWords.push(match[0]);
        } else {
            // Otherwise, split the matched string by '{{' and '}}' and add each part to the result array
            const words = match[0].split(/\{\{.*?\}\}/);
            const tags = match[0].match(/\{\{.*?\}\}/g);
            splitWords.push(...words.filter(Boolean));
            if (tags) {
                splitWords.push(...tags);
            }
        }
    }

    const tagRegex = new RegExp(tagFinderRegex, 'g');
    const seenMatches = new Set();
    for (let i = 0; i < splitWords.length; i++) {
        const [regexResult] = [...splitWords[i].matchAll(tagRegex)];
        if (regexResult) {
            const tagName = regexResult[1];
            const [nextWordRegexResult] = Array.from(splitWords[i + 1]?.matchAll(tagRegex) || []);
            if (nextWordRegexResult) {
                const adjacentTagName = nextWordRegexResult[1];
                const key = `${tagName},${adjacentTagName}`;
                if (seenMatches.has(key)) {
                    break;
                }
                matches.push([tagName, adjacentTagName]);
                seenMatches.add(key);
            }
        }
    }
    return matches;
};
